import { useCallback, useEffect, useRef, useState } from 'react';
import { Spinner } from '@heroui/react';

import { useApi } from '../lib/api-context';
import type { FileEntry } from '../lib/api-client';

const TEXTUAL_TYPES = new Set([
  'text/markdown',
  'text/plain',
  'text/html',
  'text/css',
  'text/csv',
  'text/javascript',
  'text/x-typescript',
  'text/x-python',
  'application/json',
  'application/yaml',
  'application/xml',
  'application/x-sh',
  'application/toml',
]);

interface FileExplorerProps {
  scope: 'persistent' | 'workspace';
  /** Required when `scope === 'workspace'`. */
  sessionId?: string;
  /** Track of how many times the user pressed "Refresh". */
  refreshKey: number;
  /** Open file viewer tab. */
  onOpenFile: (input: {
    scope: 'persistent' | 'workspace';
    path: string;
    sessionId?: string;
  }) => void;
}

interface DirState {
  loading: boolean;
  error: string | null;
  entries: FileEntry[];
  /** R2 pagination cursor — present when the directory has > 256 children. */
  cursor?: string;
  /** True once the user has clicked "Load more" past the last cursor. */
  exhausted: boolean;
}

/**
 * One-level-at-a-time tree explorer. Backed by the new `/api/files`
 * REST surface. Cache lives in component state so collapsing then
 * re-expanding a folder reuses the entries (until the user hits
 * Refresh on the host sidebar). Binary files appear in the listing
 * but cannot be opened.
 */
export function FileExplorer({
  scope,
  sessionId,
  refreshKey,
  onOpenFile,
}: FileExplorerProps) {
  const api = useApi();
  const [dirs, setDirs] = useState<Record<string, DirState>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({ '': true });

  // Per-path request token plus a monotonically-increasing GENERATION
  // token. The generation is bumped on every scope/session/refresh
  // reset; in-flight requests captured an older generation and bail
  // when their resolution lands. Without the generation, swapping the
  // Map for a new one would silently let an old request collide with
  // a new request that happens to use the same per-path counter (1==1)
  // and overwrite the listing for a different scope/session.
  const reqTokensRef = useRef<Map<string, number>>(new Map());
  const generationRef = useRef(0);

  const fetchDir = useCallback(
    async (path: string, options?: { append?: boolean; cursor?: string }) => {
      const append = options?.append === true;
      const myGeneration = generationRef.current;
      const myReqId = (reqTokensRef.current.get(path) ?? 0) + 1;
      reqTokensRef.current.set(path, myReqId);

      const isStale = () =>
        generationRef.current !== myGeneration ||
        reqTokensRef.current.get(path) !== myReqId;

      setDirs((prev) => ({
        ...prev,
        [path]: {
          loading: true,
          error: null,
          entries: append ? (prev[path]?.entries ?? []) : (prev[path]?.entries ?? []),
          cursor: prev[path]?.cursor,
          exhausted: prev[path]?.exhausted ?? false,
        },
      }));
      try {
        const listing = await api.listFiles(scope, path, {
          sessionId,
          cursor: options?.cursor,
        });
        if (isStale()) return;
        setDirs((prev) => {
          const previous = prev[path];
          const merged = append
            ? [...(previous?.entries ?? []), ...listing.entries]
            : listing.entries;
          return {
            ...prev,
            [path]: {
              loading: false,
              error: null,
              entries: merged,
              cursor: listing.cursor,
              exhausted: !listing.cursor,
            },
          };
        });
      } catch (err) {
        if (isStale()) return;
        setDirs((prev) => ({
          ...prev,
          [path]: {
            loading: false,
            error: err instanceof Error ? err.message : String(err),
            entries: prev[path]?.entries ?? [],
            cursor: prev[path]?.cursor,
            exhausted: prev[path]?.exhausted ?? false,
          },
        }));
      }
    },
    [api, scope, sessionId],
  );

  // Reset cache and refetch root when scope/session/refresh changes.
  useEffect(() => {
    // Bump the generation FIRST so any unresolved promise from the
    // previous scope/session bails on its `isStale()` check before
    // touching state.
    generationRef.current += 1;
    reqTokensRef.current = new Map();
    setDirs({});
    setExpanded({ '': true });
    void fetchDir('');
  }, [scope, sessionId, refreshKey, fetchDir]);

  const onToggleDir = useCallback(
    (path: string) => {
      setExpanded((prev) => {
        const next = { ...prev, [path]: !prev[path] };
        if (next[path] && !dirs[path]) {
          void fetchDir(path);
        }
        return next;
      });
    },
    [dirs, fetchDir],
  );

  const onLoadMore = useCallback(
    (path: string) => {
      const state = dirs[path];
      if (!state?.cursor) return;
      void fetchDir(path, { append: true, cursor: state.cursor });
    },
    [dirs, fetchDir],
  );

  const rootState = dirs[''];
  const rootEntries = rootState?.entries ?? [];

  return (
    <div className="scroll-pane min-h-0 flex-1 overflow-y-auto px-1 py-1 text-sm">
      {rootState?.loading && rootEntries.length === 0 && (
        <div className="flex items-center gap-2 px-3 py-2 text-xs opacity-70">
          <Spinner size="sm" /> Loading…
        </div>
      )}
      {rootState?.error && (
        <div className="px-3 py-2 text-xs text-rose-300">{rootState.error}</div>
      )}
      {!rootState?.loading && rootEntries.length === 0 && !rootState?.error && (
        <EmptyState scope={scope} />
      )}
      <ul role="tree" className="flex flex-col">
        {rootEntries.map((entry) => (
          <TreeNode
            key={entry.name}
            entry={entry}
            path={entry.name}
            depth={0}
            scope={scope}
            sessionId={sessionId}
            dirs={dirs}
            expanded={expanded}
            onToggleDir={onToggleDir}
            onOpenFile={onOpenFile}
            onLoadMore={onLoadMore}
          />
        ))}
      </ul>
      {rootState?.cursor && !rootState.exhausted && (
        <button
          type="button"
          onClick={() => onLoadMore('')}
          disabled={rootState.loading}
          className="my-1 ms-2 rounded px-2 py-0.5 text-xs opacity-70 hover:bg-white/5 hover:opacity-100 disabled:opacity-40"
        >
          {rootState.loading ? 'Loading…' : 'Load more'}
        </button>
      )}
    </div>
  );
}

interface TreeNodeProps {
  entry: FileEntry;
  path: string;
  depth: number;
  scope: 'persistent' | 'workspace';
  sessionId?: string;
  dirs: Record<string, DirState>;
  expanded: Record<string, boolean>;
  onToggleDir: (path: string) => void;
  onOpenFile: FileExplorerProps['onOpenFile'];
  onLoadMore: (path: string) => void;
}

function TreeNode({
  entry,
  path,
  depth,
  scope,
  sessionId,
  dirs,
  expanded,
  onToggleDir,
  onOpenFile,
  onLoadMore,
}: TreeNodeProps) {
  const indent = { paddingInlineStart: `${0.5 + depth * 0.85}rem` };

  if (entry.kind === 'dir') {
    const isOpen = expanded[path] === true;
    const childState = dirs[path];
    return (
      <li role="treeitem" aria-expanded={isOpen}>
        <button
          type="button"
          onClick={() => onToggleDir(path)}
          style={indent}
          className="flex w-full items-center gap-1.5 rounded py-0.5 text-start transition-colors hover:bg-white/5 focus-visible:bg-white/5 focus-visible:outline-none"
        >
          <span aria-hidden="true" className="opacity-60">
            {isOpen ? '▾' : '▸'}
          </span>
          <span aria-hidden="true">📁</span>
          <span className="truncate">{entry.name}</span>
        </button>
        {isOpen && (
          <ul role="group" className="flex flex-col">
            {childState?.loading && !childState.entries.length && (
              <li
                style={{ paddingInlineStart: `${0.5 + (depth + 1) * 0.85}rem` }}
                className="py-0.5 text-xs opacity-60"
              >
                Loading…
              </li>
            )}
            {childState?.error && (
              <li
                style={{ paddingInlineStart: `${0.5 + (depth + 1) * 0.85}rem` }}
                className="py-0.5 text-xs text-rose-300"
              >
                {childState.error}
              </li>
            )}
            {childState?.entries.map((child) => (
              <TreeNode
                key={child.name}
                entry={child}
                path={`${path}/${child.name}`}
                depth={depth + 1}
                scope={scope}
                sessionId={sessionId}
                dirs={dirs}
                expanded={expanded}
                onToggleDir={onToggleDir}
                onOpenFile={onOpenFile}
                onLoadMore={onLoadMore}
              />
            ))}
            {childState?.cursor && !childState.exhausted && (
              <li
                style={{ paddingInlineStart: `${0.5 + (depth + 1) * 0.85}rem` }}
              >
                <button
                  type="button"
                  onClick={() => onLoadMore(path)}
                  disabled={childState.loading}
                  className="rounded px-1 py-0.5 text-xs opacity-70 hover:bg-white/5 hover:opacity-100 disabled:opacity-40"
                >
                  {childState.loading ? 'Loading…' : 'Load more'}
                </button>
              </li>
            )}
            {!childState?.loading &&
              !childState?.error &&
              childState?.entries.length === 0 && (
                <li
                  style={{ paddingInlineStart: `${0.5 + (depth + 1) * 0.85}rem` }}
                  className="py-0.5 text-xs opacity-50"
                >
                  Empty
                </li>
              )}
          </ul>
        )}
      </li>
    );
  }

  const isText = isTextual(entry.mediaType);
  return (
    <li role="treeitem">
      <button
        type="button"
        disabled={!isText}
        title={isText ? entry.name : `Binary file (${entry.mediaType ?? 'unknown'})`}
        onClick={() =>
          isText && onOpenFile({ scope, path, sessionId })
        }
        style={indent}
        className={[
          'flex w-full items-center gap-1.5 rounded py-0.5 text-start transition-colors',
          isText
            ? 'hover:bg-white/5 focus-visible:bg-white/5 focus-visible:outline-none'
            : 'cursor-not-allowed opacity-40',
        ].join(' ')}
      >
        <span aria-hidden="true" className="opacity-0">▸</span>
        <span aria-hidden="true">{iconFor(entry)}</span>
        <span className="truncate">{entry.name}</span>
        {entry.size !== undefined && (
          <span className="ms-auto text-xs opacity-50">{formatSize(entry.size)}</span>
        )}
      </button>
    </li>
  );
}

function EmptyState({ scope }: { scope: 'persistent' | 'workspace' }) {
  if (scope === 'workspace') {
    return (
      <div className="px-3 py-3 text-xs opacity-70">
        No files in this session's workspace subtree yet — anything Bluebird
        saves under <code translate="no">/workspace/sessions/&lt;this-session-id&gt;/</code>
        will appear after you press Refresh.
      </div>
    );
  }
  return (
    <div className="px-3 py-3 text-xs opacity-70">
      No files under <code translate="no">/workspace/</code> yet. Anything
      Bluebird saves anywhere under <code translate="no">/workspace/</code>
      (including session subtrees) will appear after Refresh.
    </div>
  );
}

function isTextual(mediaType?: string): boolean {
  if (!mediaType) return false;
  if (TEXTUAL_TYPES.has(mediaType)) return true;
  return mediaType.startsWith('text/');
}

function iconFor(entry: FileEntry): string {
  if (entry.kind === 'dir') return '📁';
  if (entry.mediaType === 'text/markdown') return '📝';
  if (entry.mediaType?.startsWith('image/')) return '🖼';
  if (entry.mediaType === 'application/pdf') return '📄';
  if (entry.mediaType?.includes('json')) return '🧬';
  return '📄';
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export { isTextual };
